<template>
  <AlertMessage
    :alertType="alertType ? alertType : 'empty'"
    :message="alertMessage"
    @close="alertClose"
  />

  <UsersPreviewFromSpace
    :open="openUsersList"
    :spaceId="selectedSpaceId"
    :spaceNameId="selectedSpaceNameId"
    @update:open="updateOpen"
  />

  <AddSpaceForm
    :mode="formMode"
    :open="isSpaceFormVisible"
    :space="selectedSpaceForEdit"
    @alert="showAlert"
    @close="closeSpaceForm"
    @spaceAdded="refreshSpaces"
    @spaceUpdated="refreshSpaces"
  />

  <UpdateTargetUsersForSpace
    :open="isUpdateMembersVisible"
    :spaceId="selectedSpaceId"
    :spaceNameId="selectedSpaceNameId"
    @alert="showAlert"
    @close="closeUpdateMembers"
    @spaceAdded="refreshSpaces"
  />

  <ConfirmDeleteModal
    :deleteMessage="confirmMessage"
    :isOpen="isDeleteModalOpen"
    :spaceName="spaceToDelete?.name"
    titleText="Delete Space"
    @close="closeDeleteModal"
    @confirm="deleteSelectedSpace"
  />

  <DataTable
    :columns="columns"
    :current-page="currentPage"
    :data="spaces"
    :loading="loading"
    :page-size="pageSize"
    :sort-direction="sortDirection"
    :sort-field="sortField"
    :total-items="totalItems"
    :total-pages="totalPages"
    @sort="handleSort"
    @page-change="goToPage"
    @page-size-change="handlePageSizeChange"
    @prev-page="prevPage"
    @next-page="nextPage"
  >
    <template v-if="showFilters" #filters>
      <TableFilters
        :checkboxes="filterCheckboxes"
        @checkbox-change="handleCheckboxChange"
        @clear-filters="clearFilters"
      />
    </template>

    <template #cell-details="{ item }">
      <div class="flex flex-col gap-2 text-sm text-gray-500">
        <span v-if="!isExpanded(item.id)">
          {{ truncateText(item.details, 120) || "—" }}
        </span>
        <span v-else>
          {{ item.details }}
        </span>
        <button
          v-if="item.details && item.details.length > 120"
          class="self-start text-xs font-semibold uppercase tracking-wide text-primary-neutral hover:text-primary"
          @click="toggleDetails(item.id)"
        >
          {{ isExpanded(item.id) ? "Show Less" : "Show More" }}
        </button>
      </div>
    </template>

    <template #cell-actions="{ item }">
      <div class="flex items-center justify-start gap-2">
        <TableActionButton
          icon="edit"
          title="Edit Space"
          @click="editSpace(item)"
        />
        <TableActionButton
          icon="plus"
          title="Add Member"
          @click="addMember(item.id, item.chatSpaceId)"
        />
        <TableActionButton
          icon="delete"
          title="Delete"
          variant="danger"
          @click="confirmDeleteSpace(item)"
        />
      </div>
    </template>
  </DataTable>
</template>

<script>
import { deleteSpace, getAllSpaces, getSpaceById } from "@/services/space";
import AlertMessage from "@/components/AlertMessage.vue";
import DataTable from "@/components/ui/DataTable.vue";
import TableFilters from "@/components/ui/TableFilters.vue";
import TableActionButton from "@/components/ui/TableActionButton.vue";
import UsersPreviewFromSpace from "@/components/UsersPreviewFromSpace.vue";
import AddUpdateSpaceForm from "@/components/AddUpdateSpaceForm.vue";
import UpdateTargetUsersForSpace from "@/components/UpdateTargetUsersForSpace.vue";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal.vue";

export default {
  emits: ["update-total"],
  props: {
    showFilters: {
      type: Boolean,
      default: false,
    },
  },
  expose: ["toggleSpaceForm"],
  data() {
    return {
      spaces: [],
      selectedSpaceId: null,
      selectedSpaceNameId: "",
      openUsersList: false,
      isSpaceFormVisible: false,
      isUpdateMembersVisible: false,
      selectedSpaceForEdit: null,
      formMode: "add",
      expandedDetails: {},
      alertMessage: "",
      alertType: "",
      currentPage: 1,
      pageSize: 6,
      showActiveSpaces: true,
      totalItems: 0,
      loading: false,
      sortField: null,
      sortDirection: null,
      isDeleteModalOpen: false,
      spaceToDelete: null,
      confirmMessage: "",
      sortFieldMapping: {
        name: "name",
        details: "details",
      },
    };
  },
  components: {
    AlertMessage,
    DataTable,
    TableFilters,
    TableActionButton,
    UsersPreviewFromSpace,
    AddSpaceForm: AddUpdateSpaceForm,
    UpdateTargetUsersForSpace,
    ConfirmDeleteModal,
  },
  mounted() {
    this.getSpaces();
  },
  watch: {
    showActiveSpaces() {
      this.getSpaces();
    },
  },
  computed: {
    columns() {
      return [
        {
          key: "name",
          label: "Name",
          sortable: true,
          width: "260px",
          cellClass: "text-sm font-semibold text-gray-900",
          skeletonClass: "w-32",
        },
        {
          key: "details",
          label: "Description",
          sortable: true,
          cellClass: "text-sm text-gray-500",
          skeletonClass: "w-full",
        },
        {
          key: "actions",
          label: "Actions",
          sortable: false,
          width: "130px",
          headerClass: "text-left",
          cellClass: "text-left",
          skeletonClass: "w-16",
        },
      ];
    },
    filterCheckboxes() {
      return [
        { key: "active", label: "Only Active", value: this.showActiveSpaces },
      ];
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.pageSize);
    },
  },
  methods: {
    alertClose() {
      this.alertMessage = "";
      this.alertType = "";
    },
    showAlert(alert) {
      this.alertMessage = alert.message;
      this.alertType = alert.type;
    },
    updateOpen(value) {
      this.openUsersList = value;
    },
    toggleSpaceForm() {
      this.isSpaceFormVisible = !this.isSpaceFormVisible;
      this.selectedSpaceForEdit = null;
      this.formMode = "add";
    },
    closeSpaceForm() {
      this.isSpaceFormVisible = false;
      this.selectedSpaceForEdit = null;
      this.formMode = "add";
    },
    async editSpace(space) {
      try {
        const spaceData = await getSpaceById(space.id);
        this.selectedSpaceForEdit = spaceData;
        this.formMode = "edit";
        this.isSpaceFormVisible = true;
      } catch (error) {
        console.error("Failed to get space data:", error);
        this.showAlert({
          message: "Failed to load space data. Please try again later.",
          type: "dismiss",
        });
      }
    },
    toggleUpdateMembers() {
      this.isUpdateMembersVisible = !this.isUpdateMembersVisible;
    },
    closeUpdateMembers() {
      this.isUpdateMembersVisible = false;
    },
    toggleDetails(id) {
      if (this.expandedDetails[id]) {
        delete this.expandedDetails[id];
      } else {
        this.expandedDetails[id] = true;
      }
    },
    isExpanded(id) {
      return !!this.expandedDetails[id];
    },
    truncateText(text, length) {
      if (!text) return "";
      return text.length > length ? text.substring(0, length) + "..." : text;
    },
    refreshSpaces() {
      this.getSpaces();
    },
    handleCheckboxChange({ key, value }) {
      if (key === "active") {
        this.showActiveSpaces = value;
      }
    },
    handlePageSizeChange(newSize) {
      this.pageSize = newSize;
      this.currentPage = 1;
      this.getSpaces();
    },
    viewMembers(spaceId, spaceNameId) {
      this.selectedSpaceId = spaceId;
      this.selectedSpaceNameId = spaceNameId;
      this.openUsersList = true;
    },
    addMember(spaceId, spaceNameId) {
      this.selectedSpaceId = spaceId;
      this.selectedSpaceNameId = spaceNameId;
      this.isUpdateMembersVisible = true;
    },
    confirmDeleteSpace(space) {
      this.spaceToDelete = space;
      this.isDeleteModalOpen = true;
      this.confirmMessage = `Are you sure you want to delete the space "${space.name}"? This action cannot be undone.`;
    },
    closeDeleteModal() {
      this.isDeleteModalOpen = false;
      this.spaceToDelete = null;
    },
    async deleteSelectedSpace() {
      if (!this.spaceToDelete) return;

      this.loading = true;
      try {
        await deleteSpace(this.spaceToDelete.chatSpaceId);
        this.closeDeleteModal();
        this.getSpaces();
        this.showAlert({
          message: "Space deleted successfully",
          type: "success",
        });
      } catch (error) {
        console.error("Failed to delete space:", error);
        this.alertMessage = "Failed to delete space. Please try again later.";
        this.alertType = "dismiss";
      } finally {
        this.loading = false;
      }
    },
    async getSpaces() {
      this.loading = true;
      const startTime = Date.now();
      try {
        const backendSortField = this.sortField
          ? this.sortFieldMapping[this.sortField]
          : null;

        const fetchedSpaces = await getAllSpaces(
          this.showActiveSpaces ? true : undefined,
          backendSortField,
          this.sortDirection
        );

        // Apply pagination (client-side for now)
        const startIndex = (this.currentPage - 1) * this.pageSize;
        const endIndex = startIndex + this.pageSize;

        this.totalItems = fetchedSpaces.length;
        this.spaces = fetchedSpaces.slice(startIndex, endIndex);
        this.$emit("update-total", this.totalItems);
      } catch (error) {
        console.error("Failed to get spaces:", error);
        this.alertMessage = "Failed to get spaces. Please try again later.";
        this.alertType = "dismiss";
      } finally {
        const elapsedTime = Date.now() - startTime;
        const minLoadingTime = 200;
        if (elapsedTime < minLoadingTime) {
          setTimeout(() => {
            this.loading = false;
          }, minLoadingTime - elapsedTime);
        } else {
          this.loading = false;
        }
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.getSpaces();
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.getSpaces();
      }
    },
    goToPage(page) {
      this.currentPage = page;
      this.getSpaces();
    },
    handleSort(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortField = field;
        this.sortDirection = "asc";
      }
      this.currentPage = 1;
      this.getSpaces();
    },
    clearFilters() {
      this.showActiveSpaces = true;
      this.currentPage = 1;
      this.getSpaces();
    },
  },
};
</script>
